/**
 * A workflow's canvas — the desktop app's, not a variant of it.
 *
 * Every number here is the app's own (`workflow/graph-shared.ts`,
 * `StepNodeView.tsx`): a node is `NODE_WIDTH` wide with an 8px radius, a 26px
 * title band and a body padded `7px 9px 9px`, and it has **no fixed height** —
 * it grows with its instruction, so the height is measured rather than assumed
 * and the arrows are drawn against what is actually on screen.
 *
 * Two pieces of routing are load-bearing, and both were learned by drawing:
 *
 * - **`handlesFor`.** Left-to-right is the default, because a chain reads across
 *   the canvas. What that cannot draw is a step and the step *below* it: an
 *   arrow out of the right into a left directly underneath has to swing around
 *   both nodes, and a pair of them — a check's `fail` going down, the repair's
 *   arrow coming back up — crosses in an X exactly where the labels saying
 *   which is which sit. So a near-vertical pair leaves the bottom and enters the
 *   top, and the two directions take **opposite thirds** of the node so they run
 *   parallel instead of over each other.
 * - **`selfLoopPath`.** A step's arrow back into itself arches `LOOP_HEIGHT`
 *   over the node and swings `LOOP_REACH` past the handles, with its label at
 *   the top of the arch.
 *
 * React Flow draws this in the app; here the paths are written out, because a
 * graph library is a hundred kilobytes to render nine boxes nobody drags for
 * long. The bezier is the same shape: a control point half the distance out,
 * with a floor so two nodes almost touching still curve.
 */
import { useCallback, useState } from 'react';
import type React from 'react';
import type { WorkflowEdge, WorkflowStep } from '../../../lib/types';
import { color, font, radius, shadow } from '../../../theme/tokens';

/** Fixed, so a chain of six steps reads as a row of the same thing. */
export const NODE_WIDTH = 210;

/** Of the instruction, on the node. The inspector holds the rest. */
export const NODE_INSTRUCTION_CHARS = 120;

/** How far over its node a self-loop arches, and how far past the handles it swings. */
const LOOP_HEIGHT = 78;
const LOOP_REACH = 56;

/** What an arrow says, and what it is drawn in. */
export const WHEN_LABEL: Record<WorkflowEdge['when'], string> = {
  always: 'then',
  pass: 'on pass',
  fail: 'on fail',
  // The desktop's newer arrows (2026-10-27): drawn here as the app draws them, made on the app's canvas.
  each: 'for each',
  case: 'case',
  error: 'on error'
};
// The redesign's arrows (§45.2): then #86868B, on pass teal, on fail red and
// dashed 4 3 — the word on the arrow carries the sense, the colour doubles it.
const WHEN_COLOR: Record<WorkflowEdge['when'], string> = {
  always: color.lineInput,
  pass: color.accent,
  fail: color.red,
  each: color.purple,
  case: color.blue,
  error: color.yellow
};
const WHEN_INK: Record<WorkflowEdge['when'], string> = {
  always: color.inkFaint,
  pass: color.accentText,
  fail: color.badText,
  each: color.inkFaint,
  case: color.inkFaint,
  error: color.warnText
};

/** Clicking an arrow's label walks this, which is the whole vocabulary — each and case are the app's, and go back to then. */
export const WHEN_NEXT: Record<WorkflowEdge['when'], WorkflowEdge['when']> = {
  always: 'pass',
  pass: 'fail',
  fail: 'error',
  error: 'always',
  each: 'always',
  case: 'always'
};

/** Where an arrow leaves and arrives, from where its two steps actually sit. */
function handlesFor(from: WorkflowStep | undefined, to: WorkflowStep | undefined): 'across' | 'down' | 'up' {
  const across = from && to ? Math.abs(to.x - from.x) : Infinity;
  // Half a node's width is the point at which a bezier between the sides stops
  // being the shorter drawing.
  if (!from || !to || across > NODE_WIDTH / 2 || to.y === from.y) return 'across';
  return to.y > from.y ? 'down' : 'up';
}

interface Point {
  x: number;
  y: number;
}

function anchors(from: WorkflowStep, to: WorkflowStep, height: (id: string) => number): [Point, Point, 'across' | 'down' | 'up'] {
  const way = handlesFor(from, to);
  const hf = height(from.id);
  const ht = height(to.id);
  if (way === 'down') {
    return [
      { x: from.x + NODE_WIDTH * 0.3, y: from.y + hf },
      { x: to.x + NODE_WIDTH * 0.3, y: to.y },
      way
    ];
  }
  if (way === 'up') {
    return [
      { x: from.x + NODE_WIDTH * 0.7, y: from.y },
      { x: to.x + NODE_WIDTH * 0.7, y: to.y + ht },
      way
    ];
  }
  return [
    { x: from.x + NODE_WIDTH, y: from.y + hf / 2 },
    { x: to.x, y: to.y + ht / 2 },
    way
  ];
}

/** A cubic between two anchors, and the point halfway along it for the label. */
function curve(a: Point, b: Point, way: 'across' | 'down' | 'up'): [string, Point] {
  const reach = Math.max(Math.abs(way === 'across' ? b.x - a.x : b.y - a.y) * 0.5, 30);
  const c1 = way === 'across' ? { x: a.x + reach, y: a.y } : { x: a.x, y: a.y + (way === 'down' ? reach : -reach) };
  const c2 = way === 'across' ? { x: b.x - reach, y: b.y } : { x: b.x, y: b.y + (way === 'down' ? -reach : reach) };
  const mid = {
    x: (a.x + 3 * c1.x + 3 * c2.x + b.x) / 8,
    y: (a.y + 3 * c1.y + 3 * c2.y + b.y) / 8
  };
  return [`M ${a.x},${a.y} C ${c1.x},${c1.y} ${c2.x},${c2.y} ${b.x},${b.y}`, mid];
}

/** Out of the right handle, over the node, back into the left one. */
function selfLoop(step: WorkflowStep, height: number): [string, Point] {
  const sourceX = step.x + NODE_WIDTH;
  const sourceY = step.y + height / 2;
  const targetX = step.x;
  const targetY = sourceY;
  const topY = Math.min(sourceY, targetY) - LOOP_HEIGHT;
  const midX = (sourceX + targetX) / 2;
  return [
    `M ${sourceX},${sourceY} ` +
      `C ${sourceX + LOOP_REACH},${sourceY} ${sourceX + LOOP_REACH},${topY} ${midX},${topY} ` +
      `C ${targetX - LOOP_REACH},${topY} ${targetX - LOOP_REACH},${targetY} ${targetX},${targetY}`,
    { x: midX, y: topY }
  ];
}

/* ── what a node says about itself ──────────────────────────────────────── */

/**
 * The check, in the app's own words.
 *
 * The verb is **checks**, and there is no `check:` prefix — a tag that labelled
 * itself would spend a third of the node's width saying what the word after it
 * already says.
 */
export function describeCheck(check: WorkflowStep['check']): string {
  if (check.scope === 'command') return check.command.trim() || 'no command';
  if (check.scope === 'changed') return 'checks what changed';
  if (check.scope === 'all') return 'checks every project';
  return '';
}

/** What it runs as: the pair, joined by a middle dot. */
export const describeRunAs = (step: WorkflowStep): string => [step.model, step.effort].filter(Boolean).join(' · ');

/**
 * A step's chip (§45.2): mono, 22 high — the model a neutral fill, « waits for
 * you » amber (it is a wait), everything else a hairline ring.
 */
function Tag({ children, tone = 'plain' }: { children: React.ReactNode; tone?: 'plain' | 'accent' | 'waiting' }): React.JSX.Element {
  const [background, line, ink] =
    tone === 'accent'
      ? [color.surfaceAlt, color.surfaceAlt, color.inkMuted]
      : tone === 'waiting'
        ? [color.warnBg, color.warnBg, color.warnText]
        : ['transparent', color.line, color.inkSubtle];
  return (
    <span
      style={{
        maxWidth: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 7px',
        borderRadius: 6,
        background,
        border: `1px solid ${line}`,
        color: ink,
        fontFamily: font.mono,
        fontSize: 11,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </span>
  );
}

/* ── the canvas ─────────────────────────────────────────────────────────── */

export function WorkflowCanvas({
  steps,
  edges,
  selected,
  editable,
  onSelect,
  onMove,
  onCycle,
  onUnlink
}: {
  steps: WorkflowStep[];
  edges: WorkflowEdge[];
  selected: string | null;
  editable: boolean;
  onSelect: (stepId: string | null) => void;
  onMove: (stepId: string, x: number, y: number) => void;
  onCycle: (edgeId: string) => void;
  onUnlink: (edgeId: string) => void;
}): React.JSX.Element {
  // Measured, not assumed: a node grows with its instruction, and an arrow
  // drawn against a guessed height points at the air beside the box.
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [dragging, setDragging] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  const measure = useCallback((id: string, node: HTMLDivElement | null): void => {
    if (!node) return;
    const height = node.offsetHeight;
    setHeights((current) => (current[id] === height ? current : { ...current, [id]: height }));
  }, []);

  const heightOf = (id: string): number => heights[id] ?? 118;
  const byId = new Map(steps.map((step) => [step.id, step]));

  // Which step a run starts on: the one nothing points at. A graph with none is
  // a cycle, and the app calls its first step the entry in that case too.
  const targeted = new Set(edges.map((edge) => edge.target));
  const entry = steps.find((step) => !targeted.has(step.id))?.id ?? steps[0]?.id;

  const width = Math.max(702, ...steps.map((step) => step.x + NODE_WIDTH + 60));
  const height = Math.max(436, ...steps.map((step) => step.y + heightOf(step.id) + 80));

  const drag = (step: WorkflowStep) => (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!editable || (event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    setDragging(step.id);
    const fromX = event.clientX;
    const fromY = event.clientY;
    const atX = step.x;
    const atY = step.y;
    const move = (moved: PointerEvent): void => onMove(step.id, atX + (moved.clientX - fromX), atY + (moved.clientY - fromY));
    const done = (): void => {
      setDragging(null);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', done);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', done);
  };

  return (
    // Two boxes rather than one: the outer fills whatever the row is tall and
    // scrolls, the inner is the size of the graph. One box would have to be
    // both, and it would be whichever the last edit made it.
    <div
      onClick={() => onSelect(null)}
      className="nsb"
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 420,
        // The page's tile (§45.2, rule 42): #F5F5F7, radius 14, a #D2D2D7 dot
        // grid on a 20 pitch.
        borderRadius: 14,
        background: color.surfaceAlt,
        backgroundImage: 'radial-gradient(#D2D2D7 1px, transparent 0)',
        backgroundSize: '20px 20px',
        overflow: 'auto'
      }}
    >
      <div style={{ position: 'relative', width, height }}>
      <svg width={width} height={height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
        <defs>
          {(['always', 'pass', 'fail'] as const).map((when) => (
            <marker
              key={when}
              id={`arrow-${when}`}
              viewBox="0 0 10 10"
              refX={8}
              refY={5}
              markerWidth={5}
              markerHeight={5}
              orient="auto-start-reverse"
            >
              <path d="M 1 1 L 9 5 L 1 9" fill="none" stroke={WHEN_COLOR[when]} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
            </marker>
          ))}
        </defs>
        {edges.map((edge) => {
          const from = byId.get(edge.source);
          const to = byId.get(edge.target);
          if (!from || !to) return null;
          const [path] =
            edge.source === edge.target ? selfLoop(from, heightOf(from.id)) : curve(...anchors(from, to, heightOf));
          return (
            <path
              key={edge.id}
              d={path}
              fill="none"
              stroke={WHEN_COLOR[edge.when]}
              strokeWidth={1.4}
              strokeDasharray={edge.when === 'fail' ? '4 3' : undefined}
              markerEnd={`url(#arrow-${edge.when})`}
            />
          );
        })}
      </svg>

      {/* The labels sit over the lines rather than in the SVG: they are
          buttons — clicking one walks always → on pass → on fail — and a
          `<foreignObject>` for three words is a second layout engine. */}
      {edges.map((edge) => {
        const from = byId.get(edge.source);
        const to = byId.get(edge.target);
        if (!from || !to) return null;
        const [, mid] = edge.source === edge.target ? selfLoop(from, heightOf(from.id)) : curve(...anchors(from, to, heightOf));
        const shown = hovered === edge.id;
        return (
          <span
            key={edge.id}
            onPointerEnter={() => setHovered(edge.id)}
            onPointerLeave={() => setHovered((current) => (current === edge.id ? null : current))}
            style={{ position: 'absolute', left: mid.x, top: mid.y, transform: 'translate(-50%,-50%)', display: 'flex', gap: 4 }}
          >
            <button
              type="button"
              disabled={!editable}
              title={editable ? 'What this arrow waits for — click to change' : undefined}
              onClick={(event) => {
                event.stopPropagation();
                onCycle(edge.id);
              }}
              // A white pill on a hairline with its dot (§45.2): a ring for then,
              // teal for on pass, red for on fail; hovered, the #F5F5F7 ground
              // and a #86868B line, as a button that walks the three words.
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 22,
                padding: '0 8px',
                borderRadius: 999,
                border: `1px solid ${editable && shown ? color.lineInput : color.line}`,
                background: editable && shown ? color.surfaceAlt : color.surface,
                color: WHEN_INK[edge.when],
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                cursor: editable ? 'pointer' : 'default',
                transition: 'background 150ms ease, border-color 150ms ease'
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: edge.when === 'always' ? 'transparent' : WHEN_COLOR[edge.when],
                  boxShadow: edge.when === 'always' ? `inset 0 0 0 1.5px ${color.lineInput}` : 'none'
                }}
              />
              {WHEN_LABEL[edge.when]}
            </button>
            {editable && (
              <button
                type="button"
                title="Remove this arrow"
                onClick={(event) => {
                  event.stopPropagation();
                  onUnlink(edge.id);
                }}
                style={{
                  width: 18,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  borderRadius: radius.chip,
                  border: `1px solid ${color.line}`,
                  background: color.surface,
                  color: color.inkGhost,
                  cursor: 'pointer',
                  opacity: shown ? 1 : 0,
                  transition: 'opacity 90ms ease'
                }}
              >
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                  <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
                </svg>
              </button>
            )}
          </span>
        );
      })}

      {steps.map((step, index) => {
        const isSelected = step.id === selected;
        const runsAs = describeRunAs(step);
        const checkLabel = describeCheck(step.check);
        const unreachable = step.id !== entry && !targeted.has(step.id);
        const border = isSelected ? color.accent : unreachable ? color.orange : color.line;
        return (
          <div
            key={step.id}
            ref={(node) => measure(step.id, node)}
            onPointerDown={drag(step)}
            onClick={(event) => {
              event.stopPropagation();
              onSelect(step.id);
            }}
            style={{
              position: 'absolute',
              left: step.x,
              top: step.y,
              width: NODE_WIDTH,
              borderRadius: 12,
              background: color.surface,
              // A node is the one thing on the canvas that floats: a hairline in
              // its state's colour and the card shadow, and a 2px teal ring
              // when it is the one being edited (§45.2).
              boxShadow: `inset 0 0 0 ${isSelected ? 2 : 1}px ${border}, ${shadow.card}`,
              transition: 'box-shadow 150ms ease',
              overflow: 'hidden',
              cursor: editable ? (dragging === step.id ? 'grabbing' : 'grab') : 'pointer',
              userSelect: 'none'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 32,
                padding: '0 12px',
                boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
              }}
            >
              <span style={{ flex: 'none', fontFamily: font.mono, fontSize: 11, color: color.inkSubtle }}>{index + 1}</span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: step.name ? color.ink : color.inkGhost
                }}
              >
                {step.name || 'Untitled step'}
              </span>
              {step.id === entry && (
                <span style={{ flex: 'none', fontFamily: font.mono, fontSize: 11, color: color.accentText }}>start</span>
              )}
            </div>
            <div style={{ padding: '9px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span
                style={{
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: step.instruction ? color.inkMuted : color.inkGhost,
                  // Three lines of the instruction (§45.2); the inspector holds the rest.
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {step.instruction
                  ? step.instruction.length > NODE_INSTRUCTION_CHARS
                    ? `${step.instruction.slice(0, NODE_INSTRUCTION_CHARS)}…`
                    : step.instruction
                  : 'No instruction yet.'}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {runsAs ? <Tag tone="accent">{runsAs}</Tag> : null}
                {step.conversation === 'new' ? <Tag>new conversation</Tag> : null}
                {step.conversation !== 'main' && step.conversation !== 'new' ? (
                  <Tag>{step.conversation.slice(0, 24)}</Tag>
                ) : null}
                {step.annotate ? <Tag>notes on code</Tag> : null}
                {step.gate === 'ask' ? <Tag tone="waiting">waits for you</Tag> : null}
                {checkLabel ? <Tag>{checkLabel.slice(0, 30)}</Tag> : null}
              </div>
              {unreachable && (
                <span style={{ fontSize: 12, color: color.warnText }}>Not connected — never reached.</span>
              )}
            </div>
          </div>
        );
      })}

      {steps.length === 0 && (
        <p style={{ position: 'absolute', left: 24, top: 24, margin: 0, fontSize: 13, color: color.inkFaint }}>
          Nothing here yet. A workflow is a chain of instructions a task is driven by.
        </p>
      )}
      </div>
    </div>
  );
}
